import { useCallback } from 'react'

import {
  AccountSettingsControllerService,
  AccountSettingsForm,
  ProfilePictureService,
} from '@api'
import { useMutation, useQueryClient } from 'react-query'

import { useToast } from '@/components/ui/toast.tsx'
import { logoService } from '@/modules/accounts/service/logo-service'
import { handleError } from '@/utils/error'

const updatePicture = async (data: Blob): Promise<any> => {
  // BE type require string[]
  return ProfilePictureService.addProfilePicture({
    requestBody: data as unknown as string[],
  })
}

export const useUpdateSettings = () => {
  const queryClient = useQueryClient()
  const toast = useToast()
  const invalidateCache = useCallback(() => {
    queryClient.refetchQueries('account')
  }, [queryClient])

  const updatePictureMutation = useMutation(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ({ ...data }: any) => {
      const { image } = data

      return updatePicture(image)
    },
    {
      onSettled: () => invalidateCache(),
      onError: (error) => handleError(error, 'Failed to update picture'),
    },
  )

  const updateAgencyLogoMutation = useMutation(
    ({ ...data }: { image: Blob; mime?: string; isDarkMode?: boolean }) => {
      const { image, isDarkMode, mime } = data

      return logoService.updateAgencyLogo(image, isDarkMode, mime)
    },
    {
      onSettled: () => {
        invalidateCache()
      },
    },
  )

  const resetAgencyLogoMutation = useMutation(
    () => {
      return logoService.resetAgencyLogo()
    },
    {
      onSettled: () => {
        invalidateCache()
      },
    },
  )

  const updateProfileMutation = useMutation(
    (data: AccountSettingsForm) =>
      AccountSettingsControllerService.updateAccount({ requestBody: data }),
    {
      onSuccess: () => toast.success('Saved changes'),
      onError: (error) => handleError(error, 'Failed to update profile'),
      onSettled: () => invalidateCache(),
    },
  )

  return {
    updatePictureMutation,
    updateAgencyLogoMutation,
    updateProfileMutation,
    resetAgencyLogoMutation,
  }
}
